<template>
    <main ref="root" role="main" class="flex flex-col mt-[3.9rem] md:mt-14">

      <!-- Hero Banner -->
      <section
        ref="heroSection"
        class="home-section--hero"
        @mousemove="onHeroMouseMove"
        @mouseleave="onHeroMouseLeave"
      >
        <div class="hero-dot-grid" aria-hidden="true"></div>
        <div class="hero-dot-reveal" aria-hidden="true"></div>
        <div class="hero-spotlight" aria-hidden="true"></div>
        <div class="container">
          <h1 class="home-title" v-html="t('home.title')"></h1>
          <p class="home-subtitle" v-html="t('home.superpower')"></p>
          <Customers />
        </div>
      </section>

      <!-- Projects -->
      <section class="card-section">
        <div class="container">
          <div class="section-header reveal">
            <p class="section-label">{{ t('work.sectionLabel') }}</p>
            <h2 class="section-title">{{ t('work.sectionTitle') }}</h2>
            <p class="section-desc">{{ t('work.sectionDesc') }}</p>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-4">
            <Project
              v-for="(project, idx) in $tm('work.showcase-cards')"
              :key="idx"
              :content="project"
              :class="{ reveal: idx >= 2, 'reveal-mobile': idx === 1 }"
              :style="idx >= 2 ? { transitionDelay: (idx - 2) * 80 + 'ms' } : {}"
            />
          </div>
        </div>
      </section>

      <!-- Skills -->
      <section class="skills-section reveal">
        <div class="container">
          <div class="section-header">
            <p class="section-label">{{ t('about.skillsLabel') }}</p>
            <h2 class="section-title">{{ t('about.skillsTitle') }}</h2>
            <p class="section-desc">{{ t('about.skillsDesc') }}</p>
          </div>
          <div class="skills-tags">
            <div v-for="tag in skillTags" :key="tag.label" class="skill-tag">
              <img :src="tag.icon" :alt="tag.label" class="skill-tag-icon" />
              <span class="skill-tag-label">{{ tag.label }}</span>
            </div>
          </div>
        </div>
      </section>

    </main>
</template>

<script>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Customers from '../components/clientsSection.vue'
import Project from '../components/cardProject.vue'
import { useScrollReveal } from '../composables/useScrollReveal'
import { skillTags } from '../data/skillTags'

export default {
  name: 'Home-main',
  components: { Customers, Project },
  methods: {
    onHeroMouseMove(e) {
      const el = this.$refs.heroSection
      if (!el) return
      const rect = el.getBoundingClientRect()
      el.style.setProperty('--mx', (e.clientX - rect.left) + 'px')
      el.style.setProperty('--my', (e.clientY - rect.top) + 'px')
    },
    onHeroMouseLeave() {
      const el = this.$refs.heroSection
      if (!el) return
      el.style.setProperty('--mx', '-9999px')
      el.style.setProperty('--my', '-9999px')
    }
  },
  setup() {
    const { t, locale } = useI18n()
    const root = ref(null)
    useScrollReveal(root)
    return { t, locale, root, skillTags }
  }
}
</script>

<style scoped>

.section-header {
  margin-bottom: var(--space-10);
}

.section-label {
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  color: var(--text-tertiary);
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  margin-bottom: var(--space-xs);
}

.section-title {
  font-family: var(--font-display);
  font-size: var(--text-xl);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-snug);
  color: var(--text-primary);
  margin-bottom: var(--space-sm);
}

@media (min-width: 768px) {
  .section-title {
    font-size: var(--text-3xl);
  }
}

.section-desc {
  font-size: var(--text-base);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
}

.container {
  width: 100%;
  max-width: var(--grid-max-width);
  margin-inline: auto;
  padding-inline: var(--grid-padding);
}

/* .home-section {
  padding-top: var(--space-8);
  padding-bottom: var(--space-8);
  width: 100%;
} */

.home-section--hero {
  padding-top: var(--space-xl);
  padding-bottom: var(--space-xl);
  background-color: var(--surface-raised);
  position: relative;
  overflow: hidden;
}

.dark .home-section--hero {
  background-color: var(--surface-bg-dark);
}

/* Keeps text and logos above the overlay layers */
.home-section--hero > .container {
  position: relative;
  z-index: 1;
}

/* Static dot grid */
.hero-dot-grid {
  position: absolute;
  inset: 0;
  z-index: 0;
  background-image: radial-gradient(circle, rgba(26, 127, 90, 0.05) 2px, transparent 2px);
  background-size: 26px 26px;
  pointer-events: none;
}

.dark .hero-dot-grid {
  background-image: radial-gradient(circle, rgba(26, 127, 90, 0.25) 2px, transparent 2px);
}

/* Brighter dot layer revealed under the cursor */
.hero-dot-reveal {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, rgba(26, 127, 90, 0.35) 2px, transparent 2px);
  background-size: 26px 26px;
  -webkit-mask-image: radial-gradient(180px circle at var(--mx, -9999px) var(--my, -9999px), black 0%, transparent 100%);
  mask-image: radial-gradient(180px circle at var(--mx, -9999px) var(--my, -9999px), black 0%, transparent 100%);
  pointer-events: none;
}

.dark .hero-dot-reveal {
  background-image: radial-gradient(circle, rgba(33, 216, 150, 0.45) 2px, transparent 2px);
}

/* Soft green glow that follows the cursor */
.hero-spotlight {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    50px circle at var(--mx, -9999px) var(--my, -9999px),
    /* rgba(33, 216, 150, 0.07) 0%, */
    transparent 70%
  );
  pointer-events: none;
}

.skills-section,
.card-section {
  padding-block: var(--space-3xl);
  width: 100%;
}
.skills-section {
  padding-top: var(--space-0);
}

.home-title {
  font-family: var(--font-display);
  line-height: var(--leading-tight);
  letter-spacing: var(--tracking-snug);
  color: var(--color-brand-dark);
  margin-bottom: var(--space-6);
  max-width: 800px;
}

.dark .home-title {
  color: var(--color-neutral-100);}

.home-title :deep(.title-greeting) {
  display: block;
  font-size: var(--text-2xl);
  font-weight: var(--weight-semibold);
  line-height: 1.1;
  color: var(--color-brand-accent-dim);
}

.home-title :deep(.title-role) {
  display: block;
  font-size: var(--text-3xl);
  font-weight: var(--weight-black);
  line-height: 1.1;
}

@media (min-width: 768px) {
  .home-section--hero {
    padding-top: var(--space-4xl);
    padding-bottom: var(--space-4xl);
  }

  .home-title :deep(.title-greeting) { 
      font-size: var(--text-4xl);
  }

  .home-title :deep(.title-role) {
    font-size: var(--text-5xl);
  }
  
}

.home-title :deep(.title-role-accent) {
  color: var(--color-brand-primary);
}

.home-subtitle {
  font-size: var(--text-xl);
  font-weight: var(--weight-bold);
  color: var(--text-secondary);
  max-width: 475px;
  line-height: var(--leading-snug);
  letter-spacing: var(--tracking-snug);
  margin-bottom: var(--space-10);
}

@media (min-width: 576px) {
  .home-section--hero{
    padding-block: var(--space-2xl)
  }
  .home-subtitle {
    font-size: var(--text-2xl);
    max-width: 625px;
  }
}

@media (min-width: 768px) {
  .home-subtitle {
    font-size: var(--text-3xl);
    max-width: 800px;
  }
    .home-section--hero{
    padding-block: var(--space-4xl)
  }
}

.skills-tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  max-width: 890px;
}

.skill-tag {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  background: var(--surface-raised);
  border: 1px solid var(--border-default);
  border-radius: var(--space-xs);
  padding: var(--space-xs) var(--space-sm);
  transition:
    transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 220ms ease;
}

.skill-tag:hover {
  transform: translateY(-4px);
}

@media (min-width: 768px) {
  .skil-tags {
    gap: var(--space-5);
  }
  .skill-tag {
    gap: var(--space-sm);
    padding: var(--space-sm) var(--space-md);
  }
}

.skill-tag-icon {
  width: 25px;
  height: 25px;
  object-fit: contain;
  flex-shrink: 0;
}

.skill-tag-label {
  font-family: var(--font-sans);
  font-size: var(--text-xs);
  color: var(--text-secondary);
  white-space: nowrap;
  line-height: 1.7;
}

@media (min-width: 768px) {
  .skill-tag-label {
    font-size: var(--text-sm);
  }
}

/* Mobile-only reveal — hidden on small screens, immediately visible on md+ */
.reveal-mobile {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.reveal-mobile.visible {
  opacity: 1;
  transform: translateY(0);
}

@media (min-width: 768px) {
  .reveal-mobile {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .reveal-mobile {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* Scroll reveal */
.reveal {
  opacity: 0;
  transform: translateY(44px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.reveal.visible {
  opacity: 1;
  transform: translateY(0);
}


@media (prefers-reduced-motion: reduce) {
  .reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>